import React from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Stack,
} from '@chakra-ui/react';
import { LuEye, LuEyeOff } from 'react-icons/lu';

const MIN_PASSWORD_LENGTH = 8;

interface EditPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (newPassword: string, newPasswordConfirm: string) => Promise<void> | void;
}

export const EditPasswordDialog: React.FC<EditPasswordDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [errors, setErrors] = React.useState<{ password?: string; confirmPassword?: string }>({});
  const [submitting, setSubmitting] = React.useState(false);

  const reset = React.useCallback(() => {
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setErrors({});
    setSubmitting(false);
  }, []);

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) reset();
    onOpenChange(details.open);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors: { password?: string; confirmPassword?: string } = {};
    if (password.length < MIN_PASSWORD_LENGTH) {
      nextErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }
    if (confirmPassword !== password) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    void (async () => {
      setSubmitting(true);
      try {
        await onSubmit(password, confirmPassword);
        onOpenChange(false);
        reset();
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          password:
            error instanceof Error && error.message
              ? error.message
              : 'Unable to update password. Please try again.',
        }));
      } finally {
        setSubmitting(false);
      }
    })();
  };

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={handleOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner px={{ base: '4', md: '6' }} py={{ base: '4', md: '8' }}>
          <Dialog.Content w="full" maxW="md" mx="4" borderRadius="l3" overflow="hidden">
            <Dialog.Header px="6" pt="6" pb="4">
              <Dialog.Title>Set Password</Dialog.Title>
            </Dialog.Header>

            <Box asChild>
              <form onSubmit={handleSubmit} noValidate>
                <Dialog.Body px="6" pb="4">
                  <Stack gap={4}>
                    <Field.Root invalid={Boolean(errors.password)} required>
                      <Field.Label>
                        New Password
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Box position="relative" w="full">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          pr="10"
                        />
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((prev) => !prev)}
                          position="absolute"
                          right="1"
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex={1}
                        >
                          <Icon as={showPassword ? LuEyeOff : LuEye} />
                        </IconButton>
                      </Box>
                      {errors.password ? (
                        <Field.ErrorText>{errors.password}</Field.ErrorText>
                      ) : (
                        <Field.HelperText>
                          Must be at least {MIN_PASSWORD_LENGTH} characters.
                        </Field.HelperText>
                      )}
                    </Field.Root>

                    <Field.Root invalid={Boolean(errors.confirmPassword)} required>
                      <Field.Label>
                        Confirm Password
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                      />
                      {errors.confirmPassword ? (
                        <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>
                      ) : null}
                    </Field.Root>
                  </Stack>
                </Dialog.Body>

                <Dialog.Footer px="6" pb="6">
                  <HStack gap={3} justify="flex-end" w="full">
                    <Button
                      type="button"
                      variant="outline"
                      colorPalette="gray"
                      disabled={submitting}
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" colorPalette="river" loading={submitting}>
                      Save
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </form>
            </Box>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
